import { Link } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import PlaceholderBanner from './PlaceholderBanner'

function Section({ heading, paragraphs = [], list = [], table = null, note = null }) {
  return (
    <div className="mb-10">
      {heading && (
        <h2 className="text-xl font-display font-semibold text-void-100 mb-3">{heading}</h2>
      )}
      {paragraphs.map((p, i) => (
        <p key={i} className="text-void-300 leading-relaxed mb-3">{p}</p>
      ))}
      {list.length > 0 && (
        <ul className="space-y-2 mb-3">
          {list.map((item, i) => (
            <li key={i} className="text-void-300 leading-relaxed flex items-start gap-2">
              <span className="text-synth-500 mt-1.5 shrink-0">&bull;</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
      {table && (
        <div className="overflow-x-auto mb-3 rounded-xl border border-void-800/50">
          <table className="w-full text-sm text-left">
            <thead className="bg-void-900/60">
              <tr>
                {table.headers.map((h, i) => (
                  <th key={i} className="px-4 py-2 text-void-200 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, i) => (
                <tr key={i} className="border-t border-void-800/40">
                  {row.map((cell, j) => (
                    <td key={j} className="px-4 py-2 text-void-400 align-top">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {note && (
        <p className="text-amber-300/90 text-sm bg-amber-950/30 border border-amber-800/40 rounded-lg px-4 py-2 mt-2">
          {note}
        </p>
      )}
    </div>
  )
}

export default function LegalPageLayout({
  title,
  subtitle,
  effectiveDate,
  lastUpdated,
  bannerText,
  intro = [],
  sections = [],
  backTo = '/corporate',
  backLabel = 'Back to Corporate',
  relatedLinks = [],
}) {
  return (
    <div className="pt-24 pb-16 min-h-screen">
      <div className="fixed inset-0 bg-gradient-to-br from-synth-950/30 via-void-950 to-purple-950/20 -z-10" />
      <div className="fixed inset-0 grid-overlay opacity-20 -z-10" />

      {bannerText && <PlaceholderBanner>{bannerText}</PlaceholderBanner>}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        <Link
          to={backTo}
          className="inline-flex items-center gap-2 text-void-400 hover:text-void-200 transition-colors mb-8"
        >
          <ChevronLeft className="w-4 h-4" />
          {backLabel}
        </Link>

        <h1 className="text-3xl md:text-4xl font-display font-bold text-void-50 mb-2">
          {title}
        </h1>
        {subtitle && <p className="text-lg text-void-400 mb-4">{subtitle}</p>}
        <p className="text-sm text-void-500 mb-10">
          {effectiveDate && <>Effective Date: {effectiveDate}<br /></>}
          {lastUpdated && <>Last Updated: {lastUpdated}</>}
        </p>

        {intro.map((p, i) => (
          <p key={i} className="text-void-300 leading-relaxed mb-4">{p}</p>
        ))}

        {relatedLinks.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-10">
            {relatedLinks.map((link, i) => (
              <Link
                key={i}
                to={link.to}
                className="text-sm text-synth-400 hover:text-synth-300 underline underline-offset-4"
              >
                {link.label}
              </Link>
            ))}
          </div>
        )}

        {sections.map((s, i) => (
          <Section key={i} {...s} />
        ))}
      </div>
    </div>
  )
}
